import { Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import MetricCard from './metricCard';
import '../styles/companionAppUsageReport.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserGroup, faClock, faChartLine } from '@fortawesome/free-solid-svg-icons';

const DataSummaryMetrics = ({
  dataSummary,
  isLoading = false,
  yearOverYearComparison = false
}) => {
  if (isLoading) {
    return (
      <Row className="g-3 mb-3">
        {[1, 2, 3, 4].map((index) => (
          <Col lg={6} xl={3} key={index}>
            <MetricCard
              title="Loading..."
              currentValue="-"
              isLoading={isLoading}
              yearOverYearComparison={yearOverYearComparison}
            />
          </Col>
        ))}
      </Row>
    );
  }

  if (!dataSummary) {
    return null;
  }

  const metrics = [
    {
      key: 'dailyActiveUsers',
      title: 'Daily Active Users',
      helpText: (
        <>
          <h6>Daily Active Users</h6>
          <span className="mb-0">
            Average daily active users value for the selected range.
          </span>
        </>
      ),
      icon: <FontAwesomeIcon icon={faUserGroup} style={{ width: '16px', height: '16px' }} />
    },
    {
      key: 'monthlyActiveUsers',
      title: 'Monthly Active Users',
      helpText: (
        <>
          <h6>Monthly Active Users</h6>
          <span className="mb-0">
            Average number of unique users active within a month.
          </span>
        </>
      ),
      icon: <FontAwesomeIcon icon={faUserGroup} style={{ width: '16px', height: '16px' }} />
    },
    {
      key: 'averageSessionLength',
      title: 'Avg Session Length',
      unit: ' min',
      helpText: (
        <>
          <h6>Avg Session Length</h6>
          <span className="mb-0">
            Average duration of user sessions in minutes. A session ends at logout or 30 min idle.
          </span>
        </>
      ),
      icon: <FontAwesomeIcon icon={faClock} />
    },
    {
      key: 'sessionsPerUser',
      title: 'Sessions Per User',
      helpText: (
        <>
          <h6>Sessions Per User</h6>
          <span className="mb-0">
            Average number of sessions each user started in range. A session ends at logout or 30 min idle.
          </span>
        </>
      ),
      icon: <FontAwesomeIcon icon={faChartLine} />
    }
  ];

  return (
    <Row className="g-3 mb-3 metric-cards-row">
      {metrics.map((metric) => {
        const metricData = dataSummary[metric.key];
        if (!metricData) return null;

        const changePercent = metricData.previous !== 0 && metricData.previous !== null
          ? ((metricData.current - metricData.previous) / metricData.previous) * 100
          : 0;

        return (
          <Col lg={6} xl={3} key={metric.key}>
            <MetricCard
              metricKey={metric.key}
              title={metric.title}
              currentValue={metricData.current?.toLocaleString() || 0}
              previousValue={metricData.previous?.toLocaleString() || 0}
              changePercent={changePercent}
              yearOverYearComparison={yearOverYearComparison}
              unit={metric.unit || ''}
              helpText={metric.helpText}
              icon={metric.icon}
              isLoading={isLoading}
            />
          </Col>
        );
      })}
    </Row>
  );
};

DataSummaryMetrics.propTypes = {
  dataSummary: PropTypes.shape({
    dailyActiveUsers: PropTypes.shape({
      current: PropTypes.number,
      previous: PropTypes.number
    }),
    monthlyActiveUsers: PropTypes.shape({
      current: PropTypes.number,
      previous: PropTypes.number
    }),
    averageSessionLength: PropTypes.shape({
      current: PropTypes.number,
      previous: PropTypes.number
    }),
    sessionsPerUser: PropTypes.shape({
      current: PropTypes.number,
      previous: PropTypes.number
    }),
    day1Retention: PropTypes.shape({
      current: PropTypes.number,
      previous: PropTypes.number
    }),
    day7Retention: PropTypes.shape({
      current: PropTypes.number,
      previous: PropTypes.number
    }),
    day30Retention: PropTypes.shape({
      current: PropTypes.number,
      previous: PropTypes.number
    }),
    hasDeclineDetected: PropTypes.bool
  }),
  isLoading: PropTypes.bool,
  yearOverYearComparison: PropTypes.bool
};

export default DataSummaryMetrics;
